import { Vector3, Quaternion } from '@babylonjs/core/Maths/math';
import { Ray } from '@babylonjs/core/Culling/ray';
import { Lerp, LerpAngle } from '@babylonjs/core/Maths/math.scalar.functions';
import { WEAPONS, WEAPON_ORDER, resolveAttack, pickLoco, CLIP_MPS } from './weapons';
import { LeftHandIK } from './handik';
import { convergeAim, resolveInside } from './aim.mjs';

// Tuned toward the clips' natural stride speeds (CLIP_MPS) so the stride-match
// rate stays near 1 — crouch/walk were far faster than their clips and skated.
const SPEED = { walk: 1.4, run: 4.4, sprint: 6.9, crouch: 1.1, crouchRun: 2.6, prone: 0.35 };
const GRAVITY = -19;
const UP_AXIS = new Vector3(0, 1, 0);
const HEAD_SCALE = new Vector3(1, 1, 1); // scratch for the ADS head clear
const JUMP_V = 6.4;

// ----------------------------------------------------------------- hitscan
// Metres a round still carries. This was the hard-coded `new Ray(o, d, 300)`
// length while the tracer stopped at 80 m; both now read the same number, and
// a per-weapon `range` in weapons.js overrides it the day one is added.
const GUN_RANGE = 300;
// Shorter than this there is no tracer to draw — the muzzle is touching what
// it hit, and a zero-length tracer quad has no orientation.
const TRACER_MIN = 0.25;
// AIM_MIN_CONVERGE and the two rules that use it live in ./aim.mjs, so the
// geometry that decides where every round goes can be proven in plain Node
// (tools/shottest.mjs). This file imports Babylon and therefore cannot be.

// The metadata shapes that mean "this mesh IS a body": dummies.js and
// zombies.js park { dummy, headshot } on their hitboxes, animals.js
// { animal }, remotes.js { netPlayer }, and a bodies source may hand the
// infected proxy ({ infected, owner }) straight over. Anything else is
// scenery — a mesh carrying unrelated metadata must not read as flesh.
const targetTag = (md) => (md && (md.dummy || md.animal || md.netPlayer
  || md.infected != null) ? md : null);

// Entity hitboxes are resolved by BOTH answers. main.js now wires a
// `bodiesNear` source, so the query itself reports remote players, infected
// and animals — but the mesh pick stays, because it is the only thing that
// knows a POSED rig: zombies.js's three capsules track the broken-pose skull
// and a prone remote's capsules lie down, while a body record is an upright
// cylinder. The pick is clamped to the query's hit distance in fireShot(), so
// an entity behind a wall is not hittable through it either way. 'ground' and
// 'crate' are the GYM's own meshes (non-map mode, where there is no query to
// ask) and keep their old soil/hard surfaces.
//
// A Babylon predicate REPLACES the default liveness test (isPickable /
// isEnabled / isVisible), it does not run alongside it — so a predicate that
// only looks at metadata re-exposes every mesh those flags were hiding: a
// retired hitbox on a corpse, a remote player's capsules before the entity has
// spawned or after it died, a pooled capsule parked off to one side. Each one
// is a ghost hitbox standing exactly where the entity used to be. Put the
// liveness test back in front of the metadata test.
const pickableTarget = (m) => m.isPickable !== false && m.isEnabled()
  && (!!targetTag(m.metadata) || m.name === 'ground' || m.name === 'crate');

// effects.impact() understands exactly three cases today (effects.js:542):
// 'glass', soft === 'soil', and everything else as a hard spark. The query
// speaks the full SURFACES vocabulary, so the hard tags ('concrete', 'wood',
// 'metal', 'rock', 'asphalt') and 'glass' are passed through VERBATIM — they
// already render correctly and are ready for a widened effects.js — while the
// soft ground tags and flesh are folded onto 'soil', which is the same look
// they had before and the right one (a round into grass throws dirt, not
// sparks). When effects.js learns the whole vocabulary this fold is deleted
// and `hit.surface` goes straight in; nothing else changes.
const SOFT_IMPACT = new Set(['grass', 'forest', 'sand', 'snow', 'dirt', 'water', 'flesh']);
const impactSurface = (s) => (SOFT_IMPACT.has(s) ? 'soil' : s);

// The character root is a TransformNode we move/rotate; the mesh + Mixamo
// skeleton hang under it. Stances: stand | crouch | prone — all real clips.
// Weapons are held as upper-body layers so everything works in every stance.
export class PlayerController {
  constructor(root, meshPivot, boneMap, animator, input, hud, camera, world) {
    this.root = root;
    this.pivot = meshPivot;
    this.boneMap = boneMap;
    this.leftHandIK = new LeftHandIK(boneMap); // weld the support hand to the foregrip
    this.animator = animator;
    this.input = input;
    this.hud = hud;
    this.camera = camera;
    this.world = world; // { scene, dummies, effects, props }

    this.stance = 'stand';
    this.vy = 0;
    this.airborne = false;
    this.heading = 0;
    this.velocity = new Vector3();
    this.roll = null;          // { dir, t, dur, fwd }
    this.vault = null;         // { t, dur, from, to, apex } — scripted klatre/vault-bue
    this.transitioning = false;
    this.dead = false;
    this.lastMove = '—';
    this.focus = 0;
    this.lean = 0;
    this.stepAcc = 0;          // metres walked since last footstep sound
    this._stepPingT = 0;       // s until a footfall may raise a noise ping again
    this.wasAirborne = false;
    this.freelookYaw = null;   // camera yaw captured when Alt free-look began

    this.weapon = 'fists';
    this.equipT = 0;
    this.aiming = false;
    this.reloading = false;
    this.firing = false;       // auto-fire loop engaged
    this.fireCd = 0;
    this.ammo = { pistol: WEAPONS.pistol.mag, rifle: WEAPONS.rifle.mag };
    this.attack = null;        // { move, elapsed, dur, hits: [{t, done}] }
    this.moveLockT = 0;
    this.combatIdleT = 0;

    input.on('jump', () => this.tryJump());
    input.on('toggleCrouch', () => this.setStance(this.stance === 'crouch' ? 'stand' : 'crouch'));
    input.on('toggleProne', () => this.setStance(this.stance === 'prone' ? 'crouch' : 'prone'));
    input.on('roll', ({ dir }) => this.tryRoll(dir));
    input.on('die', () => this.toggleDead());
    input.on('attack', (e) => this.tryAttack(e));
    input.on('weapon', ({ index }) => this.setWeapon(WEAPON_ORDER[index]));
    input.on('weaponCycle', ({ dir }) => {
      const i = (WEAPON_ORDER.indexOf(this.weapon) + dir + WEAPON_ORDER.length) % WEAPON_ORDER.length;
      this.setWeapon(WEAPON_ORDER[i]);
    });
    input.on('reload', () => this.tryReload());
    input.on('gunMelee', () => this.tryGunMelee());
  }

  get wpn() { return WEAPONS[this.weapon]; }

  // ------------------------------------------------------------- weapons
  setWeapon(key) {
    if (!key || key === this.weapon || this.dead || this.transitioning) return;
    this.weapon = key;
    this.aiming = false;
    this.reloading = false;
    this.firing = false;
    this.attack = null;
    this.animator.cancelShots();
    this.world.props.setActive(key === 'fists' ? null : key);
    const wpn = WEAPONS[key];
    if (wpn.equip && this.animator.has(wpn.equip)) {
      const rate = this.animator.dur(wpn.equip) / 0.9;
      this.animator.playUpper(wpn.equip, rate);
      this.equipT = 0.75;
    } else {
      this.equipT = 0.3;
    }
    this.hud.flashMove(wpn.label);
  }

  tryReload() {
    const wpn = this.wpn;
    if (wpn.kind !== 'gun' || this.reloading || this.dead || this.equipT > 0) return;
    if (this.ammo[this.weapon] >= wpn.mag) return;
    this.reloading = true;
    this.firing = false;
    const key = this.weapon;
    const clip = this.stance === 'prone' ? wpn.reloadProne : wpn.reload;
    const rate = this.animator.dur(clip) / (this.stance === 'prone' ? 2.4 : 2.0);
    this.world.sfx?.play(`gun.reload_${key}`, { vol: 0.35, slice: 2.4, jitter: 0.04 });
    this.hud.flashMove('Reloading…');
    this.animator.playUpper(clip, rate, () => {
      if (this.weapon === key && this.reloading) {
        this.ammo[key] = wpn.mag;
        this.reloading = false;
      }
    });
  }

  tryGunMelee() {
    const wpn = this.wpn;
    if (wpn.kind !== 'gun' || this.dead || this.reloading || this.equipT > 0) return;
    if (this.attack && this.attack.elapsed < this.attack.dur * 0.55) return;
    const move = resolveAttack(this.weapon, {});
    const rate = move.rate;
    const dur = this.animator.dur(move.clip) / rate;
    this.animator.playUpper(move.clip, rate);
    this.attack = { move, elapsed: 0, dur, hits: move.hits.map(f => ({ t: f * dur, done: false })) };
    this.lastMove = move.label;
    this.hud.flashMove(move.label);
  }

  // ------------------------------------------------------------- melee
  tryAttack({ chain, dirs }) {
    if (this.dead || this.roll || this.transitioning || this.equipT > 0) return;
    if (this.wpn.kind === 'gun') { this.tryFire(); return; }
    if (this.attack && this.attack.elapsed < this.attack.dur * 0.55) return;

    const ctx = {
      dirs, chain, stance: this.stance, airborne: this.airborne,
      blocking: this.input.block, sprint: dirs.sprint && this.stance === 'stand',
    };
    let move;
    if (this.stance === 'prone') {
      if (this.weapon !== 'knife') { this.hud.flashMove('Cannot melee while prone (knife only)'); return; }
      move = { clip: 'knife-stab', label: 'Prone Stab', rate: 1.2, lunge: 0, rooted: false, hits: [0.4], reach: 1.6, dmg: 18 };
    } else {
      move = resolveAttack(this.weapon, ctx);
      if (this.stance === 'crouch' && move.rooted) {
        move = resolveAttack(this.weapon, { ...ctx, dirs: { w: 0, a: 0, s: 0, d: 0 }, chain: 0, sprint: false, airborne: false, blocking: false });
      }
    }
    if (!move) return;

    const dur = this.animator.dur(move.clip) / move.rate;
    const moving = this.velocity.lengthSquared() > 0.3;
    if (move.rooted && this.stance === 'stand') {
      this.animator.playFull(move.clip, move.rate);
      this.moveLockT = dur * 0.9;
    } else if (moving || this.stance !== 'stand') {
      this.animator.playUpper(move.clip, move.rate);
    } else {
      this.animator.playFull(move.clip, move.rate);
    }
    this.attack = { move, elapsed: 0, dur, hits: move.hits.map(f => ({ t: f * dur, done: false })) };
    this.combatIdleT = 4;
    this.lastMove = move.label;
    this.hud.flashMove(move.label + (chain ? ` (chain ${chain + 1})` : ''));
  }

  // ------------------------------------------------------------- guns
  tryFire() {
    const wpn = this.wpn;
    if (this.reloading || this.equipT > 0 || this.dead) return;
    if (this.ammo[this.weapon] <= 0) {
      this.world.sfx?.play('gun.dry', { vol: 0.25, slice: 0.5 });
      this.tryReload();
      return;
    }
    if (wpn.auto) this.firing = true;
    this.fireShot();
  }

  fireShot() {
    const wpn = this.wpn;
    if (this.fireCd > 0 || this.ammo[this.weapon] <= 0) return;
    this.fireCd = 60 / wpn.rpm;
    this.ammo[this.weapon]--;

    if (!wpn.auto) {
      const rate = this.animator.dur(wpn.fire) / 0.45;
      this.animator.playUpper(wpn.fire, rate);
    }

    // Aim direction is the render camera's, so the round always leaves along
    // the crosshair. Hip spread is applied here, exactly as before, so the
    // dispersion the player feels is unchanged.
    const camPos = this.camera.cam.globalPosition;
    const aimDir = this.camera.forward();
    if (!this.aiming) {
      const s = wpn.spreadHip;
      aimDir.x += (Math.random() - 0.5) * s; aimDir.y += (Math.random() - 0.5) * s; aimDir.z += (Math.random() - 0.5) * s;
      aimDir.normalize();
    }
    const range = wpn.range || GUN_RANGE;
    const muzzle = this.world.props.muzzleWorld();
    const q = this.world.query;

    // THE ORIGIN/DIRECTION PAIR: the crosshair is the CAMERA's line, the round
    // is the MUZZLE's, and they converge on what the camera ray found. Two
    // query rays per shot; at 540 rpm that is 18 rays/s, ~1.5 ms of CPU per
    // second of sustained fire. src/player/aim.mjs carries the whole argument
    // — why it is not the camera ray, why it is not the raw camera direction
    // either, and the near-range dead band — and is proven in plain Node by
    // tools/shottest.mjs, which this file cannot be (it imports Babylon).
    let dir = aimDir;
    if (q) {
      // The camera is BEHIND the muzzle, so its ray must reach past the
      // bullet's own range to find an aim point for the far end of it.
      const lead = range + (this.camera.dist || 4) + 1;
      const look = q.ray(camPos, aimDir, lead);
      const a = convergeAim(camPos, aimDir, muzzle, look, lead);
      if (a) dir = new Vector3(a.x, a.y, a.z);
    }

    // The world answer (terrain, walls, doors honouring open state, props,
    // trunks and bodies), then the entity meshes CLAMPED to it: a round that
    // already stopped in a wall must not reach the body behind it.
    //
    // A muzzle inside a collider is a MOVEMENT PAD far more often than it is
    // buried geometry, and the old answer (dist 0: no tracer, no replication,
    // no damage) silently deleted the round. aim.mjs resolveInside() moves the
    // hit out to the collider's far face instead, capped so it can never
    // become shoot-through. The mesh pick is clamped to the same distance, so
    // nothing beyond the collider is reachable either way.
    const hit = resolveInside(q ? q.ray(muzzle, dir, range) : null, muzzle, dir);
    const pick = this._pickTarget(muzzle, dir, hit ? hit.dist : range);

    // One answer: whichever of the two is nearer.
    const useP = !!pick && (!hit || pick.distance < hit.dist);
    const landed = useP || !!hit;
    const dist = useP ? pick.distance : hit ? hit.dist : range;
    const tag = useP ? targetTag(pick.pickedMesh.metadata)
      : hit && hit.kind === 'body' ? targetTag(hit.entity) : null;
    const end = useP ? pick.pickedPoint
      : hit ? new Vector3(hit.point.x, hit.point.y, hit.point.z)
        : muzzle.add(dir.scale(range));
    this.world.effects.muzzle(muzzle, dir);
    // A muzzle genuinely buried in solid geometry is still a hit at dist 0
    // (query.ray `inside`, past resolveInside's cap): the round stops there,
    // but there is no tracer to draw and nothing to replicate — a zero-length
    // tracer quad has no orientation to take. The far commoner case, a muzzle
    // sitting in a movement pad, now has a real distance and does both.
    const drawTracer = !landed || dist > TRACER_MIN;
    if (drawTracer) this.world.effects.tracer(muzzle, end);
    this.world.sfx?.play(`gun.${this.weapon}`, { vol: 0.65, slice: 1.1, jitter: 0.05 });
    if (Math.random() < 0.7) this.world.sfx?.play('gun.shell', { vol: 0.07, slice: 0.8, delay: 0.35 });
    if (landed) {
      const nrm = useP ? (pick.getNormal(true) || undefined)
        : new Vector3(hit.normal.x, hit.normal.y, hit.normal.z);
      // The query's own surface tag decides the decal, so a round into a
      // concrete wall no longer looks like one into soil and a round into a
      // body does not spark. The gym's two meshes keep their old pair.
      const surface = useP
        ? (tag ? 'flesh' : pick.pickedMesh.name === 'ground' ? 'soil' : 'hard')
        : hit.surface;
      this.world.effects.impact(end, nrm, impactSurface(surface));
      // `dist` is metres along the ray from the MUZZLE, which is what both the
      // falloff and the speed-of-sound delay wanted all along. Flesh does not
      // ricochet (this was `!meta.dummy`, which only ever covered dummies and
      // infected — an animal or a remote player sparked like concrete).
      if (dist > 5 && !tag) {
        const role = Math.random() < 0.3 ? 'gun.ricochet' : 'gun.impact';
        this.world.sfx?.play(role, { vol: Math.min(0.3, 3.5 / dist), slice: 0.9, delay: dist / 340 });
      }
      this._damageTarget(tag, wpn.dmg, dir, useP ? null : hit.zone);
    }
    this.world.animals?.scare(this.root.position, 60); // gunshots spook the wildlife
    // …and every infected inside 250 m (180 m for a pistol, 90 m suppressed)
    // hears it and comes looking (docs/INFECTED.md Z27, Z118). This is the REAL
    // call site: the server's `{k:'shot'}` relay only ever charged pistol rate.
    this.world.noise?.gunshot(this.weapon, this.root.position.x, this.root.position.z, !!wpn.suppressed);
    // replicate the tracer to other players (not a degenerate one, see above)
    if (drawTracer) this.world.onShot?.(muzzle, end);
    this.camera.addRecoil(wpn.kick * (this.aiming ? 0.75 : 1));
    if (this.ammo[this.weapon] <= 0) { this.firing = false; this.tryReload(); }
  }

  // Mesh pick for the POSED hit surfaces the query's upright cylinders cannot
  // represent — zombies.js's three capsules follow the broken-pose skull, a
  // prone remote's follow the rig — and for the gym's ground/crate (see
  // `pickableTarget`). `limit` is the distance the world already stopped the
  // round at, which is what gives an entity behind a wall cover.
  _pickTarget(origin, dir, limit) {
    if (!(limit > 0)) return null;
    const pick = this.world.scene.pickWithRay(new Ray(origin, dir, limit), pickableTarget);
    // Ray.length should have clipped this already; assert it anyway, because
    // this comparison is the only thing giving entities cover.
    return pick?.hit && pick.distance <= limit + 1e-3 ? pick : null;
  }

  // One damage dispatcher for both answers. `tag` is the shape the hitbox
  // meshes already carry in `metadata` — { dummy, headshot } / { animal } /
  // { netPlayer } / { infected, owner } — and main.js's `bodiesNear` source
  // puts that SAME object in `hit.entity`, which is why wiring bodies into the
  // query needed no change here. `zone` is the query's 'head' | 'body' |
  // 'legs' (null for a mesh pick, which carries its own `headshot` flag per
  // hitbox mesh).
  //
  // THE ZONE HAS TO BE FORWARDED, not just folded into `head`. Both callees
  // take it as a fifth argument (dummies.js damage(), zombies.js gunHit()) and
  // zombies.js gives an explicit zone precedence over the capsule's own tag,
  // because 'legs' has no other producer: the query's body records are the only
  // thing that knows a round landed below INF_ZONE_Y.legsTop, and on the query
  // path the tag is main.js's { infected, owner } with no `zone` of its own.
  // Dropping it meant INF.limbMul never applied, `e.legs` never accumulated
  // toward INF.legCripple, no infected was ever crippled into a crawler by
  // gunfire, and zombies.js sprayed blood from the chest for a shot in the
  // shin — i.e. the whole legs capsule / INF_ZONE_Y / crawler chain had no
  // producer at all in map mode.
  _damageTarget(tag, dmg, dir, zone) {
    if (!tag) return;
    const head = zone === 'head' || !!tag.headshot;
    if (tag.dummy) {
      // dummies.js forwards an { infected, owner } proxy to Infected.gunHit,
      // which is how the infected take gun damage — do not shortcut it.
      this.world.dummies?.damage(tag.dummy, dmg, dir, head, zone);
      this.hud.hitmarker(head);
    } else if (tag.animal) {
      this.world.animals?.damage(tag.animal, dmg);
      this.hud.hitmarker(false);
    } else if (tag.netPlayer) {
      this.world.onPlayerHit?.(tag.netPlayer, dmg, 'gun');
      this.hud.hitmarker(false);
    } else if (tag.infected != null && tag.owner) {
      // a bodies source handing the infected proxy over directly
      tag.owner.gunHit(tag, dmg, dir, head, zone);
      this.hud.hitmarker(head);
    }
  }

  // ------------------------------------------------------------- stances
  setStance(s) {
    if (this.dead || this.airborne || this.transitioning || s === this.stance) return;
    const from = this.stance;
    this.stance = s;
    const trans = {
      'stand>crouch': 'stand_to_crouch', 'crouch>stand': 'crouch_to_stand',
      'crouch>prone': 'crouch_to_prone', 'prone>crouch': 'prone_to_crouch',
      'stand>prone': 'crouch_to_prone', 'prone>stand': 'prone_to_crouch',
    }[from + '>' + s];
    if (trans) this.playTransition(trans);
  }

  playTransition(name) {
    this.transitioning = true;
    const rate = Math.max(1.3, this.animator.dur(name) / 0.8);
    this.animator.playFull(name, rate, () => { this.transitioning = false; });
  }

  tryJump() {
    if (this.dead || this.airborne || this.transitioning) return;
    if (this.stance === 'prone') { this.setStance('crouch'); return; }
    if (this.stance === 'crouch') { this.setStance('stand'); return; }
    if (this.tryVault()) return; // lav forhindring foran → vault i stedet for hop
    this.vy = JUMP_V;
    this.airborne = true;
    const moving = this.velocity.lengthSquared() > 4;
    const clip = this.weapon === 'rifle' ? 'rifle-jump' : moving ? 'jump_running' : 'jump';
    const rate = Math.max(1, this.animator.dur(clip) / 1.15);
    this.animator.playFull(clip, rate);
  }

  // Forward-probe mod pushOut-kolliderne (hegn/mure/karme/props i én kæde):
  // en lav forhindring med fri luft over → vault over/igennem; en terrænkant
  // 0.4–1.5 m → mantle op. Probes med testpunkter, så ALT der kan skubbe
  // spilleren også kan vaultes.
  tryVault() {
    if (!this.world.blocked || this.vault || this.roll || this.aiming || this.reloading) return false;
    const p = this.root.position;
    const gy = this.world.getGroundY ? this.world.getGroundY(p.x, p.z) : 0;
    const dir = new Vector3(Math.sin(this.heading), 0, Math.cos(this.heading));
    // sweep flere afstande — tynde hegn/karme må ikke smutte mellem punkter.
    // Højdebånd måles relativt til jorden VED HVERT PROBEPUNKT (dramatisk
    // terræn: et hegn på en skråning ligger i sin egen lokale højde).
    const band = (distList, h0, h1) => {
      for (const d of distList) {
        const qx = p.x + dir.x * d, qz = p.z + dir.z * d;
        const g0 = this.world.getGroundY ? this.world.getGroundY(qx, qz) : 0;
        const base = Math.min(g0, gy); // brug laveste jord — forhindringer står på deres egen
        if (this.world.blocked(qx, qz, base + h0, Math.max(g0, gy) + h1)) return true;
      }
      return false;
    };
    const AHEAD = [0.3, 0.5, 0.7, 0.9, 1.1]; // 0,2 m-trin: tynde hegn må ikke smutte imellem
    this.lastVaultReject = 'no-obstacle';
    if (band(AHEAD, 0.25, 1.15)) {
      // find overkanten: laveste h (over SPILLERENS jord) hvor båndet er frit
      const freeAt = (distList, h0, h1) => {
        for (const d of distList) {
          const qx = p.x + dir.x * d, qz = p.z + dir.z * d;
          if (this.world.blocked(qx, qz, gy + h0, gy + h1)) return false;
        }
        return true;
      };
      let top = null;
      for (let h = 0.45; h <= 1.95; h += 0.15) {                              // stakitter er ~1,6 m m. margin
        if (freeAt(AHEAD, h, h + 0.3)) { top = h; break; }
      }
      if (top == null) { this.lastVaultReject = 'solid'; return false; }
      if (!freeAt(AHEAD, top + 0.3, top + 1.05)) { this.lastVaultReject = 'clearance'; return false; }
      if (!freeAt([1.7, 2.0], top + 0.3, top + 0.95)) { this.lastVaultReject = 'thick'; return false; }
      const land = new Vector3(p.x + dir.x * 2.05, 0, p.z + dir.z * 2.05);
      land.y = this.world.getGroundY ? this.world.getGroundY(land.x, land.z) : 0;
      if (gy + top - land.y > 2.6) { this.lastVaultReject = 'drop'; return false; }
      this.startVault(land, gy + top, top > 1.1 ? 'climb-top' : 'vault');
      return true;
    }
    // terrænkant: jorden foran er markant højere og fri for forhindringer
    const aheadY = this.world.getGroundY ? this.world.getGroundY(p.x + dir.x * 1.3, p.z + dir.z * 1.3) : 0;
    const rise = aheadY - gy;
    if (rise > 0.4 && rise <= 1.5 && !band([1.3], aheadY + 0.2, aheadY + 1.6)) {
      const land = new Vector3(p.x + dir.x * 1.15, aheadY, p.z + dir.z * 1.15);
      this.startVault(land, aheadY, rise > 1.1 ? 'climb-top' : 'vault');
      return true;
    }
    return false;
  }

  startVault(to, topY, clipWanted) {
    const clip = this.animator.has(clipWanted) ? clipWanted
      : this.animator.has('vault') ? 'vault' : null;
    const dur = clipWanted === 'climb-top' ? 1.0 : 0.75;
    if (clip) this.animator.playFull(clip, Math.max(0.8, this.animator.dur(clip) / dur));
    this.vault = {
      t: 0, dur,
      from: this.root.position.clone(),
      to,
      apex: Math.max(this.root.position.y, topY) + 0.45,
    };
    this.vy = 0;
    this.airborne = false;
    this.firing = false;
    this.attack = null;
    this.hud.flashMove(clipWanted === 'climb-top' ? 'Climb' : 'Vault');
    this.world.audio?.step(0.3, 1);
    // scrambling over a fence is a 12 m scuffle (Z27, Z118)
    this.world.noise?.vault(this.root.position.x, this.root.position.z);
  }

  tryRoll(dir) {
    if (this.dead || this.airborne || this.roll || this.transitioning) return;
    let clip, speed, fwd = false;
    if (this.stance === 'prone') {
      if (dir === 0) dir = 1;
      clip = 'prone-roll'; speed = 2.0;
    } else if (dir === 0) {
      clip = this.input.sprint ? 'roll-sprint' : 'roll-fwd'; speed = 4.5; fwd = true;
    } else {
      clip = 'dodge-right'; speed = 3.4;
    }
    const rate = Math.max(1, this.animator.dur(clip) / 0.85);
    const dur = this.animator.playFull(clip, rate, () => { this.roll = null; });
    this.roll = { dir, t: 0, dur: dur || 0.8, speed, fwd };
    this.hud.flashMove(this.stance === 'prone' ? 'Prone Roll' : fwd ? 'Combat Roll' : 'Dodge');
  }

  toggleDead() {
    if (!this.dead) {
      this.dead = true;
      this.aiming = false; this.firing = false; this.reloading = false; this.attack = null;
      const name = this.stance === 'prone' ? 'prone_death'
        : this.stance === 'crouch' ? 'crouch_death'
        : ['death', 'death_front', 'death_fwd', 'death_right', 'death_back_hs', 'death_front_hs'][Math.floor(Math.random() * 6)];
      this.animator.die(name);
      this.hud.flashMove('DEAD — press K to respawn');
    } else {
      this.dead = false;
      this.animator.revive();
      this.animator.setBase('idle');
    }
  }

  // ------------------------------------------------------------- per-frame
  update(dt, cameraYaw) {
    this.fireCd = Math.max(0, this.fireCd - dt);
    this.equipT = Math.max(0, this.equipT - dt);
    this.moveLockT = Math.max(0, this.moveLockT - dt);
    this.combatIdleT = Math.max(0, this.combatIdleT - dt);

    if (this.dead) { this.animator.update(dt); return; }

    const wpn = this.wpn;
    this.aiming = wpn.kind === 'gun' && this.input.block && !this.roll && this.equipT <= 0;

    // auto fire while LMB held
    if (this.firing) {
      if (this.input.lmb && wpn.auto && !this.reloading) this.fireShot();
      else this.firing = false;
    }

    // melee hit windows
    if (this.attack) {
      this.attack.elapsed += dt;
      for (const h of this.attack.hits) {
        if (!h.done && this.attack.elapsed >= h.t) {
          h.done = true;
          const m = this.attack.move;
          this.world.sfx?.play('melee.whoosh', { vol: 0.2, slice: 0.5, jitter: 0.12 });
          let n = 0;
          if (m.reach) {
            n = this.world.dummies.meleeHit(this.root.position, this.heading, m.reach, m.dmg);
            n += this.world.animals?.meleeHit(this.root.position, this.heading, m.reach, m.dmg) ?? 0;
            n += this.world.remoteHit?.(this.root.position, this.heading, m.reach, m.dmg) ?? 0;
            if (n > 0) {
              this.hud.hitmarker(false);
              const HIT = { knife: 'melee.stab', spear: 'melee.stab', bat: 'melee.bat', axe: 'melee.stab', pistol: 'melee.bat', rifle: 'melee.bat' };
              this.world.sfx?.play(HIT[this.weapon] || 'melee.punch', { vol: 0.45, slice: 0.7, delay: 0.03 });
            }
          }
          // a swing through empty air is 8 m of whoosh; one that lands is a
          // 22 m thud (Z27) — the emitter picks the radius from `hitFlesh`
          this.world.noise?.melee(this.root.position.x, this.root.position.z, n > 0);
        }
      }
      if (this.attack.elapsed >= this.attack.dur) this.attack = null;
    }

    const axis = this.input.moveAxis();
    const moving = axis.x !== 0 || axis.z !== 0;

    // speed mode: sprint only standing; Alt = slow walk. Unaimed er AL
    // bevægelse "fremad" (kroppen vender sig), så sprint virker i alle retninger
    const fwdish = axis.z > 0; // sprint/crouch-run are forward-run clips
    let mode = 'run';
    if (this.input.sprint && this.stance === 'stand' && fwdish) mode = 'sprint';
    else if (this.input.walkSlow) mode = 'walk';
    if (this.aiming || (wpn.kind === 'melee' && this.input.block)) mode = 'walk';
    if (this.attack || this.reloading) mode = mode === 'sprint' ? 'run' : mode;

    // crouch runs fast only forward (crouched-run clip); strafe/back stay slow
    let speed = this.stance === 'crouch' ? (fwdish && mode !== 'walk' ? SPEED.crouchRun : SPEED.crouch)
      : this.stance === 'prone' ? SPEED.prone
      : SPEED[mode];
    if (this.moveLockT > 0) speed *= 0.12;

    if (this.vault) {
      // scripted bue: XZ smoothstep, Y løftes over kanten/karmen
      const v = this.vault;
      v.t += dt;
      const k = Math.min(1, v.t / v.dur);
      const e = k * k * (3 - 2 * k);
      this.root.position.x = Lerp(v.from.x, v.to.x, e);
      this.root.position.z = Lerp(v.from.z, v.to.z, e);
      const base = Lerp(v.from.y, v.to.y, e);
      const lift = Math.max(0, v.apex - Math.max(v.from.y, v.to.y));
      this.root.position.y = base + Math.sin(Math.PI * k) * lift;
      if (v.t >= v.dur) {
        this.vault = null;
        this.moveLockT = 0.12;
        this.world.audio?.land(0.35);
      }
    } else if (this.roll) {
      this.roll.t += dt;
      const k = Math.sin(Math.min(1, this.roll.t / this.roll.dur) * Math.PI); // ease in-out
      if (this.roll.fwd) {
        this.root.position.x += Math.sin(this.heading) * this.roll.speed * k * dt;
        this.root.position.z += Math.cos(this.heading) * this.roll.speed * k * dt;
      } else {
        const side = new Vector3(Math.cos(this.heading), 0, -Math.sin(this.heading));
        this.root.position.addInPlace(side.scale(this.roll.dir * this.roll.speed * k * dt));
      }
      if (this.roll.t >= this.roll.dur) this.roll = null;
    } else if (this.airborne) {
      // no air control: momentum from takeoff carries; heading is locked
      this.root.position.x += this.velocity.x * dt;
      this.root.position.z += this.velocity.z * dt;
    }
    // Alt free-look: capture the camera yaw on press and keep steering the
    // body/movement from that frozen frame while the mouse looks around.
    // Disabled while aiming (shots must follow the camera).
    if (this.input.freelook && !this.aiming) {
      if (this.freelookYaw == null) this.freelookYaw = cameraYaw;
    } else {
      this.freelookYaw = null;
    }
    const moveYaw = this.freelookYaw ?? cameraYaw;

    // Rigid third-person facing: the body always turns toward where the camera
    // looks (moveYaw), so the camera rides in the character's back at all times
    // — no latched-travel, no delayed auto-follow. Movement is camera-relative
    // and fully directional: W/S/A/D pick real forward/back/strafe clips (DayZ/
    // Arma). Alt free-look freezes moveYaw so the mouse can look around while
    // the body keeps its heading; ADS adds the small aim body-yaw offset.
    const combatFrame = this.aiming || (wpn.kind === 'melee' && this.input.block);
    const bodyYaw = moveYaw + (this.aiming ? (wpn.aimBodyYaw || 0) : 0);
    // DayZ/Arma pacing: full speed only forward; backpedal & pure strafe slower.
    if (!combatFrame && this.stance !== 'prone') {
      if (axis.z < 0) speed *= 0.6;
      else if (axis.z === 0 && axis.x !== 0) speed *= 0.78;
    }
    if (!this.vault && !this.roll && !this.airborne) {
      // snappy turn so the camera stays behind; lerped so idle turns are smooth
      this.heading = LerpAngle(this.heading, bodyYaw, Math.min(1, dt * (combatFrame ? 12 : 13)));
      if (moving) {
        const sin = Math.sin(moveYaw), cos = Math.cos(moveYaw);
        const dir = new Vector3(axis.x * cos + axis.z * sin, 0, axis.z * cos - axis.x * sin);
        if (dir.lengthSquared() > 0) dir.normalize();
        this.velocity.x = dir.x * speed;
        this.velocity.z = dir.z * speed;
        this.root.position.x += this.velocity.x * dt;
        this.root.position.z += this.velocity.z * dt;
      } else {
        this.velocity.setAll(0);
      }
    }

    // melee lunge pushes forward at strike start
    if (this.attack && this.attack.move.lunge && this.attack.elapsed < this.attack.dur * 0.25) {
      const l = this.attack.move.lunge;
      this.root.position.x += Math.sin(this.heading) * l * dt * 6;
      this.root.position.z += Math.cos(this.heading) * l * dt * 6;
    }

    // gravity / jump — ground is y=0 in the gym, sampled terrain in map mode
    // (skipped entirely during a vault: the arc owns the position, and pushOut
    // ville skubbe os ud af vindueshullet midtvejs)
    if (!this.vault) {
      const gy = this.world.getGroundY
        ? this.world.getGroundY(this.root.position.x, this.root.position.z) : 0;
      if (this.airborne || this.root.position.y > gy + 0.35) {
        this.vy += GRAVITY * dt;
        this.root.position.y += this.vy * dt;
        if (this.root.position.y <= gy) { this.root.position.y = gy; this.vy = 0; this.airborne = false; }
      } else {
        this.root.position.y = gy; // follow slopes/steps up to 0.35 m without going airborne
      }
      this.world.pushOut?.(this.root.position); // tree trunks etc. shove the capsule out
    }

    // Footsteps: distance-driven, so with stride-matched playback each sound
    // lands on an actual footfall (stride = clip metres per half cycle).
    // Stealth model: sprint loud, run normal, walk quiet, Ctrl-sneak near-
    // silent, crouch damped, prone silent (crawl).
    const hspeed = Math.hypot(this.velocity.x, this.velocity.z);
    this._stepPingT = Math.max(0, this._stepPingT - dt); // noise-ping throttle
    if (!this.airborne && !this.roll && moving && this.stance !== 'prone' && hspeed > 0.1) {
      this.stepAcc += hspeed * dt;
      const base = this.animator.base;
      const mps = base ? CLIP_MPS[base.name] : 0;
      const stride = mps ? Math.max(0.35, (mps * this.animator.dur(base.name)) / 2) : 0.8;
      if (this.stepAcc >= stride) {
        this.stepAcc -= stride;
        let vol = mode === 'sprint' ? 0.9 : mode === 'run' ? 0.5 : 0.16;
        if (this.input.sneak) vol = 0.05;
        if (this.stance === 'crouch') vol *= 0.55;
        const surf = this.world.surfaceAt?.(this.root.position.x, this.root.position.z);
        this.world.audio?.step(vol, this.stance === 'crouch' ? 0.9 : 1, surf);
        // How far a listener STANDING somewhere hears you walking is the
        // continuous radius (stance × speed × surface) that rides the 10 Hz
        // packet — Z20/Z22, not a ping. A footfall is additionally a one-shot
        // the horde turns TOWARD, but only a loud one: sprinting, or boots on
        // boards/asphalt/metal. It is charged as a rustle so a footstep can
        // never out-shout the model, and throttled well under the emitter's
        // token bucket (`_stepPingT`, ticked once per FRAME above — ticking it
        // per footfall would have made the throttle depend on stride length).
        // Sneaking, crouching and prone never ping at all.
        const hard = surf === 'wood' || surf === 'metal' || surf === 'asphalt';
        const brush = surf === 'forest' || surf === 'grass' || surf === 'snow';
        if (this._stepPingT <= 0 && !this.input.sneak && this.stance === 'stand'
            && (mode === 'sprint' || (hard && mode === 'run'))) {
          this._stepPingT = 0.45;
          // sprinting through undergrowth or over floorboards carries furthest
          const mul = mode === 'sprint' && (hard || brush) ? 1.4 : 1;
          this.world.noise?.emit('rustle', this.root.position.x, this.root.position.z, { mul });
        }
      }
    } else if (!moving) {
      this.stepAcc = 0;
    }
    if (this.wasAirborne && !this.airborne) {
      this.world.audio?.land(this.input.sneak ? 0.2 : 0.6);
      // landing hard is the same 12 m scuffle a vault is; a controlled sneak
      // landing stays inside the continuous footprint (Z20–Z27)
      if (!this.input.sneak) this.world.noise?.vault(this.root.position.x, this.root.position.z);
    }
    this.wasAirborne = this.airborne;

    this.root.rotationQuaternion = Quaternion.RotationAxis(Vector3.Up(), this.heading);

    // lean (Q/E) — camera reads this; spine twist applied in overlays
    const wantLean = (this.input.leanR ? 1 : 0) - (this.input.leanL ? 1 : 0);
    this.lean = Lerp(this.lean, wantLean, Math.min(1, dt * 9));

    // eye focus: builds when calm, off while aiming (ADS has its own zoom)
    const calm = (!moving || mode === 'walk') && !this.aiming;
    this.focus = Lerp(this.focus, calm && !this.airborne ? 1 : 0, Math.min(1, dt * (calm ? 1.6 : 6)));

    // upper-body hold: carry / aim / block / auto-fire loop
    this.animator.setHold(this._pickHold(wpn));

    // base locomotion (full-body aim set while ADS standing). Stride-match:
    // play the loop exactly as fast as the ground passes under the character
    // so the planted foot sticks instead of skating.
    if (!this.dead) {
      // body faces the camera, so locomotion is always directional — the real
      // axis picks the forward/back/strafe/diagonal clip (no forward-only remap)
      const [locoX, locoZ] = [axis.x, axis.z];
      const { name, rate } = pickLoco(this.stance, locoX, locoZ, mode,
        { aiming: this.aiming, weapon: this.weapon });
      const mps = moving ? CLIP_MPS[name] : 0;
      const spd = mps ? Math.min(1.9, Math.max(0.6, speed / mps)) : rate;
      this.animator.setBase(name, spd);
    }
    this.animator.update(dt);

    this.hud.setState({
      stance: this.stance, mode: moving ? mode : 'idle',
      airborne: this.airborne, lean: wantLean,
      block: wpn.kind === 'melee' && this.input.block,
      aiming: this.aiming, reloading: this.reloading,
      weapon: wpn.label,
      ammo: wpn.kind === 'gun' ? `${this.ammo[this.weapon]}/${wpn.mag}` : null,
      focus: this.focus, lastMove: this.lastMove,
      pos: this.root.position, anim: this.animator.base?.name || '—',
    });
  }

  _pickHold(wpn) {
    if (this.dead) return null;
    if (wpn.kind === 'gun') {
      if (this.firing && wpn.auto && !this.aiming) {
        return this.stance === 'prone' ? 'rifle-prone-fire' : wpn.fire;
      }
      if (this.aiming) {
        // standing ADS is a full-body base (see pickLoco) — no upper hold
        if (this.stance === 'stand') return null;
        return this.stance === 'crouch' ? wpn.aimCrouch : wpn.aimProne;
      }
      return this.stance === 'crouch' ? (wpn.holdCrouch || wpn.hold) : wpn.hold;
    }
    if (this.input.block && !this.airborne) return wpn.block;
    if (wpn.hold) return wpn.hold;
    return this.combatIdleT > 0 ? 'fight-idle' : null;
  }

  // called from onAfterAnimations so offsets stack on top of clip pose
  // A pose solve must never take the render loop down on a live server.
  poseWeapon(fn) {
    try { fn(); } catch (e) {
      if (!this._poseWarned) { this._poseWarned = true; console.warn('weapon pose:', e.message); }
    }
  }

  applyOverlays(dt) {
    // --- ADS head clear ---------------------------------------------------
    // Four rounds of shoving the camera sideways (shoulderX 0.33 -> 0.58 ->
    // 0.76) never got the player's own skull out of the aim frame, because the
    // anchor travels with the camera and the head travels with the anchor: the
    // blind judge measured it still eating ~30% of the crop with the ear
    // touching the rear sight. Stop moving the camera and stop drawing the
    // head — the standard third-person-shooter answer. Collapsing the head BONE
    // takes the skull, the knit-cap shell and the mocap hat with it (all three
    // are skinned to it) without touching the rig, the clips or any mesh list,
    // and the eased blend means it is never a pop. Runs before the dead-check
    // so a death mid-aim always releases it.
    const headBone = this.boneMap.get('mixamorig:Head');
    if (headBone) {
      const want = !this.dead && this.aiming && this.wpn.kind === 'gun' ? 1 : 0;
      const prev = this._headHide ?? 0;
      this._headHide = Math.max(0, Math.min(1, prev + (want ? dt * 9 : -dt * 9)));
      if (this._headHide > 0.001 || prev > 0.001) {
        const s = 1 - 0.94 * this._headHide;
        // re-applied every frame on purpose: the clip animates the bone's
        // matrix wholesale, so a cached one-shot scale would be erased
        headBone.scaling = HEAD_SCALE.set(s, s, s);
      }
    }
    if (this.dead) return;
    const spine1 = this.boneMap.get('mixamorig:Spine1');
    const spine2 = this.boneMap.get('mixamorig:Spine2');
    // free-look: turn the neck/head toward where the camera looks so the
    // character visibly checks their surroundings while running straight
    if (this.freelookYaw != null) {
      let off = this.camera.yaw - this.heading;
      while (off > Math.PI) off -= 2 * Math.PI;
      while (off < -Math.PI) off += 2 * Math.PI;
      off = Math.max(-1.5, Math.min(1.5, off));
      const neck = this.boneMap.get('mixamorig:Neck');
      const head = this.boneMap.get('mixamorig:Head');
      for (const [b, k] of [[neck, 0.45], [head, 0.4]]) {
        if (b?.rotationQuaternion) {
          const q = Quaternion.RotationAxis(new Vector3(0, 1, 0), off * k);
          b.rotationQuaternion.multiplyInPlace(q);
        }
      }
    }
    // lean twist (sign flipped after feel feedback: Q tips the body left).
    // Doubled after "more lean" feedback: 2×0.32 rad across the spine bones
    // (~37° visible tip) so the head and gun genuinely clear a corner.
    if (Math.abs(this.lean) > 0.02) {
      for (const s of [spine1, spine2]) {
        if (s?.rotationQuaternion) {
          const q = Quaternion.RotationAxis(new Vector3(0, 0, 1), this.lean * 0.32);
          s.rotationQuaternion.multiplyInPlace(q);
        }
      }
    }
    // aim pitch: bend the chest toward where the camera looks (guns, not prone)
    if (this.wpn.kind === 'gun' && (this.aiming || this.firing) && this.stance !== 'prone') {
      const bend = this.camera.pitch * 0.42;
      for (const s of [spine1, spine2]) {
        if (s?.rotationQuaternion) {
          const q = Quaternion.RotationAxis(new Vector3(1, 0, 0), bend * 0.5);
          s.rotationQuaternion.multiplyInPlace(q);
        }
      }
      // visual sight line: converge the weapon onto the camera ray downrange
      // so ADS shows the rifle in profile pointing at the crosshair, instead
      // of an end-on slab (round-003) or clip-pose drift (round-001/002)
      const cam = this.camera.cam;
      this.poseWeapon(() => this.world.props.aimGun?.(cam.globalPosition,
        this.camera.forward(), cam.getDirection(UP_AXIS)));
    } else if (this.wpn.kind === 'gun' && this.stance !== 'prone'
      && !this.animator.busyUpper && !this.reloading && this.equipT <= 0) {
      // low-ready carry pose: the Mixamo hold clips leave the muzzle pointing
      // wherever the hand happens to face, which read as a rifle held across
      // the body at 40 degrees down-left. Skipped while a reload/melee/equip
      // clip owns the upper body — those animate the weapon deliberately.
      this.poseWeapon(() => this.world.props.carryGun?.(this.heading));
    }
    // Support hand onto the handguard. Runs LAST so it solves against the
    // final spine/lean pose, and only while the weapon is actually settled in
    // the hand (equip/holster/melee-swing frames would fight the clip).
    const twoHanded = !this.animator.busyUpper && !this.reloading && this.equipT <= 0;
    try {
      this.leftHandIK.solve(this.world.props.foregripWorld?.() ?? null, dt, twoHanded);
    } catch (e) {
      // never let a pose solve take the render loop down on a live server
      if (!this._ikWarned) { this._ikWarned = true; console.warn('left-hand IK:', e.message); }
    }
  }
}
